// src/components/FrontOfficeComp/TopAppBarNew.js
import React, { useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import ConnectionStatus from '../ConnectionStatus';

const TopAppBarNew = ({
  onLogout,
  onAgentDashboardOpen,
  onCommentsOpen,
  onToggleHealth,
  onChatOpen,
  onShortcutsHelpOpen,
  onTodayCallActivityOpen,
  isChatOpen,
  unreadMessageCount,
  healthEnabled = true,
  callStatus = 'idle', // idle | dialing | in-call | failed
  callDuration = 0,
  activeNumber = '',
  asteriskConnected = false,
  username = 'User',
  callerId = '',
  queryId = '',
  statusText = '',
  statusClassName = '',
  statusIndicatorClassName = '',
  agentMode = 'Inbound',
  agentAvailability = 'Ready',
  onAgentModeChange,
  onAgentAvailabilityChange,
  theme = 'light',
  onToggleTheme
}) => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const userMenuButtonRef = useRef(null);
  const userMenuRef = useRef(null);

  useEffect(() => {
    if (!isUserMenuOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (
        !userMenuButtonRef.current?.contains(event.target) &&
        !userMenuRef.current?.contains(event.target)
      ) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isUserMenuOpen]);

  const closePasswordModal = () => {
    if (isChangingPassword) return;
    setIsPasswordModalOpen(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setPasswordSuccess('');
  };

  const openPasswordModal = () => {
    setIsUserMenuOpen(false);
    setPasswordError('');
    setPasswordSuccess('');
    setIsPasswordModalOpen(true);
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All password fields are required.');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New password and confirmation do not match.');
      return;
    }

    setIsChangingPassword(true);
    try {
      const response = await api.post('/account/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirmation: confirmPassword
      });
      setPasswordSuccess(response.data?.message || 'Password changed successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      const validationErrors = error.response?.data?.errors;
      const firstValidationError = validationErrors
        ? Object.values(validationErrors).flat().find(Boolean)
        : '';
      setPasswordError(
        firstValidationError ||
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Password could not be changed.'
      );
    } finally {
      setIsChangingPassword(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
    const secs = (seconds % 60).toString().padStart(2, '0');
    return `${mins}:${secs}`;
  };

  const statusBadge = (() => {
    if (statusText) {
      return { text: statusText, className: statusClassName || 'bg-secondary' };
    }
    if (callStatus === 'in-call' || callStatus === 'connected') {
      return { text: 'On Call', className: 'bg-success' };
    }
    if (callStatus === 'ringing' || callStatus === 'dialing') {
      return { text: 'Ringing', className: 'bg-primary' };
    }
    if (callStatus === 'failed') return { text: 'Failed', className: 'bg-danger' };
    return { text: 'Idle', className: 'bg-secondary' };
  })();

  const spinnerClassName = statusIndicatorClassName || (() => {
    if (statusBadge.text.startsWith('Wrap Up')) return 'text-warning';
    if (statusBadge.text === 'On Call') return 'text-success';
    if (statusBadge.text === 'Ringing') return 'text-primary';
    if (statusBadge.text === 'Failed') return 'text-danger';
    return 'text-secondary';
  })();

  const normalizedStatusText = String(statusBadge.text || '').trim().toLowerCase();
  const normalizedCallStatus = String(callStatus || '').trim().toLowerCase();
  const normalizedAgentMode = String(agentMode || '').trim().toLowerCase();
  const isOutboundMode = normalizedAgentMode === 'outbound';
  const isAvailabilityLocked =
    normalizedStatusText === 'on call' ||
    normalizedStatusText === 'ringing' ||
    ['in-call', 'connected', 'ringing', 'dialing'].includes(normalizedCallStatus) ||
    isOutboundMode;

  const unreadBadgeText = unreadMessageCount > 9 ? '9+' : String(unreadMessageCount || 0);

  return (
    <>
    <nav className="navbar navbar-expand-lg navbar-light bg-white border-bottom shadow-sm sticky-top frontoffice-topbar">
      <div className="container-fluid px-2 py-2 d-flex flex-wrap align-items-center justify-content-between gap-2 frontoffice-topbar-inner">
        <div className="d-flex flex-wrap align-items-center gap-3 frontoffice-topbar-left">
          {/* <div className="position-relative d-none d-md-block">
            <i className="bi bi-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
            <input 
              type="text" 
              placeholder="Global Search (Ctrl+/)" 
              className="form-control form-control-sm ps-5 bg-light border-0" 
              style={{ width: 'clamp(160px, 18vw, 220px)' }}
            />
          </div> */}
          <button 
            onClick={onAgentDashboardOpen} 
            className="btn btn-outline-secondary btn-sm d-none d-md-flex align-items-center gap-2"
          >
            <i className="bi bi-speedometer2"></i>
            Agent Dashboard
          </button>
          <button 
            onClick={onToggleHealth} 
            className={`btn btn-sm d-none d-md-flex align-items-center gap-2 ${healthEnabled ? 'btn-outline-success' : 'btn-outline-danger'}`}
          >
            <i className="bi bi-heart-pulse"></i>
            {healthEnabled ? 'Health On' : 'Health Off'}
          </button>
          <button
            onClick={onShortcutsHelpOpen}
            className="btn btn-outline-primary btn-sm d-none d-md-flex align-items-center gap-2"
            type="button"
            title="Keyboard shortcuts help"
          >
            <i className="bi bi-keyboard"></i>
            Shortcuts
          </button>
          <button
            onClick={onTodayCallActivityOpen}
            className="btn btn-outline-primary btn-sm d-none d-md-flex align-items-center gap-2"
            type="button"
            title="View today's call activity"
          >
            <i className="bi bi-clock-history"></i>
            Today Call Activity
          </button>
        </div>
        
        <div className="d-flex flex-wrap align-items-center gap-2 small frontoffice-topbar-right">
          {/* Asterisk Connection Status */}
          <div className="d-flex align-items-center gap-2">
            <i className={`bi ${asteriskConnected ? 'bi-wifi text-success' : 'bi-wifi-off text-danger'}`}></i>
            <span className={`badge ${asteriskConnected ? 'bg-success' : 'bg-danger'}`}>
              {asteriskConnected ? 'Asterisk Connected' : 'Asterisk Offline'}
            </span>
          </div>
          
          <span className="badge bg-secondary font-monospace">ID: {queryId || '—'}</span>
          <span className="badge bg-info text-dark font-monospace">
            Caller ID: {callerId || '—'}
          </span>
          <div className="d-flex align-items-center gap-2 text-muted">
            <i className="bi bi-telephone text-secondary"></i>
            <span>{activeNumber || '0316-XXXXXXX'}</span>
          </div>
          <div className="d-flex align-items-center gap-2 bg-light px-3 py-1 rounded-pill font-monospace">
            <i className="bi bi-clock text-primary"></i>
            <span className="fw-bold">{formatTime(callDuration)}</span>
          </div>
          <div className="vr"></div>
          <div className="d-flex align-items-center gap-2">
            <div className="d-flex align-items-center">
              <span className={`spinner-grow spinner-grow-sm me-2 ${spinnerClassName}`} style={{width: '0.5rem', height: '0.5rem'}}></span>
              <span className={`fw-semibold text-white badge ${statusBadge.className}`}>{statusBadge.text}</span>
            </div>
          </div>
          <select
            className="form-select form-select-sm"
            style={{ width: 'clamp(100px, 9vw, 130px)' }}
            aria-label="Agent mode"
            value={agentMode}
            onChange={(event) => {
              if (onAgentModeChange) {
                onAgentModeChange(event.target.value);
              }
            }}
          >
            <option value="Inbound">Inbound</option>
            <option value="Outbound">Outbound</option>
          </select>
          <select
            className="form-select form-select-sm"
            style={{ width: 'clamp(100px, 9vw, 130px)' }}
            aria-label="Agent availability"
            value={agentAvailability}
            disabled={isAvailabilityLocked}
            title={isOutboundMode ? 'Switch to Inbound to change availability' : undefined}
            onChange={(event) => {
              if (onAgentAvailabilityChange) {
                onAgentAvailabilityChange(event.target.value);
              }
            }}
          >
            <option value="Ready">{isOutboundMode ? 'Outbound Ready' : 'Ready'}</option>
            <option value="Not Ready">Not Ready</option>
            <option value="Lunch">Lunch</option>
            <option value="Training">Training</option>
            <option value="Meeting">Meeting</option>
            <option value="Prayer">Prayer</option>
            <option value="Rest Room">Rest Room</option>
          </select>
          <div className="frontoffice-topbar-comms">
            <button
              onClick={onCommentsOpen}
              className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2 frontoffice-topbar-action-btn"
              type="button"
              disabled={!onCommentsOpen}
              title="Comments"
            >
              <i className="bi bi-chat-left-text"></i>
              <span>Comments</span>
            </button>
            <button
              className={`btn btn-sm d-inline-flex align-items-center gap-2 flex-shrink-0 frontoffice-topbar-action-btn frontoffice-topbar-chat-btn ${
                isChatOpen ? 'btn-primary text-white border-primary' : 'btn-outline-primary'
              }`}
              onClick={onChatOpen}
              title="Open Agent Chat"
              type="button"
            >
              <span className="frontoffice-topbar-chat-icon">
                <i className="bi bi-chat-dots"></i>
              </span>
              <span className="frontoffice-topbar-chat-label">Chat</span>
              {unreadMessageCount > 0 && (
                <span className="badge rounded-pill bg-danger frontoffice-topbar-chat-count">
                  {unreadBadgeText}
                </span>
              )}
            </button>
          </div>
          <button
            ref={userMenuButtonRef}
            onClick={() => setIsUserMenuOpen((open) => !open)}
            className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
            type="button"
            aria-haspopup="menu"
            aria-expanded={isUserMenuOpen}
          >
            <i className="bi bi-person-circle"></i>
            <span className="fw-semibold">{username}</span>
            <i className={`bi ${isUserMenuOpen ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
          </button>
        </div>
      </div>
      {isUserMenuOpen && (
        <div
          ref={userMenuRef}
          className="dropdown-menu show shadow frontoffice-profile-menu"
          role="menu"
          style={{ top: '100%', right: '0.5rem', left: 'auto' }}
        >
          <ConnectionStatus theme={theme} embedded />
          <div className="dropdown-divider"></div>
          <button className="dropdown-item" type="button" role="menuitem" onClick={openPasswordModal}>
            <i className="bi bi-key me-2"></i>
            Change Password
          </button>
          <button
            className="dropdown-item"
            type="button"
            role="menuitem"
            onClick={onToggleTheme}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            <i className={`bi ${theme === 'dark' ? 'bi-sun' : 'bi-moon-stars'} me-2`}></i>
            {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
          </button>
          <div className="dropdown-divider"></div>
          <button
            className="dropdown-item text-danger"
            type="button"
            role="menuitem"
            onClick={() => {
              setIsUserMenuOpen(false);
              onLogout();
            }}
          >
            <i className="bi bi-box-arrow-right me-2"></i>
            Logout
          </button>
        </div>
      )}
    </nav>
    {isPasswordModalOpen && (
      <>
        <div className="modal-backdrop show" style={{ zIndex: 1990 }}></div>
        <div
          className="modal show d-block"
          style={{ zIndex: 2000 }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="change-password-title"
        >
          <div className="modal-dialog modal-dialog-centered">
            <form className="modal-content" onSubmit={handlePasswordSubmit}>
              <div className="modal-header">
                <h5 className="modal-title" id="change-password-title">Change Password</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={closePasswordModal}
                  disabled={isChangingPassword}
                ></button>
              </div>
              <div className="modal-body">
                {passwordError && <div className="alert alert-danger py-2">{passwordError}</div>}
                {passwordSuccess && <div className="alert alert-success py-2">{passwordSuccess}</div>}
                <div className="mb-3">
                  <label className="form-label" htmlFor="current-password">Current Password</label>
                  <input
                    id="current-password"
                    className="form-control"
                    type="password"
                    autoComplete="current-password"
                    value={currentPassword}
                    onChange={(event) => setCurrentPassword(event.target.value)}
                    disabled={isChangingPassword}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label" htmlFor="new-password">New Password</label>
                  <input
                    id="new-password"
                    className="form-control"
                    type="password"
                    autoComplete="new-password"
                    minLength={6}
                    maxLength={50}
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                    disabled={isChangingPassword}
                    required
                  />
                </div>
                <div>
                  <label className="form-label" htmlFor="confirm-password">Confirm New Password</label>
                  <input
                    id="confirm-password"
                    className="form-control"
                    type="password"
                    autoComplete="new-password"
                    minLength={6}
                    maxLength={50}
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    disabled={isChangingPassword}
                    required
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={closePasswordModal} disabled={isChangingPassword}>
                  Close
                </button>
                <button type="submit" className="btn btn-primary" disabled={isChangingPassword}>
                  {isChangingPassword ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </>
    )}
    </>
  );
};

export default TopAppBarNew;
